import { useCallback, useRef, useState, type KeyboardEvent } from 'react'
import { Button, Input } from '@highlight/ui'
import { X } from 'lucide-react'
import { Popover, PopoverAnchor, PopoverContent } from '@renderer/chat/components/radix/popover'
import type { Skill } from '@renderer/chat/types'
import { useOnSidepanelHidden } from '@renderer/overlay/atoms'
import { useSidebarSearchQuery, useSidebarSearchSkillId, useSidebarSkillHighlight } from './sidebar-search-atoms'
import { useSidebarSlashSkills } from './use-sidebar-slash-skills'
import { useStartChatAboutQuery } from './use-start-chat-about-query'
import { SidebarAddMenu } from './sidebar-add-menu'
import { SidebarModelMenu } from './sidebar-model-menu'
import { SidebarSearchAttachments } from './sidebar-search-attachments'
import { SidebarSkillPopup } from './sidebar-skill-popup'

// The ring utility reads `--focus-ring`, which the theme computes once from the blue, so both are set here.
const YELLOW_FOCUS_RING =
  '[--focus-ring-color:var(--yellow-9)] [--focus-ring:oklch(from_var(--yellow-9)_l_c_h_/_var(--focus-ring-alpha))]'

const keepFocusInBar = (event: Event) => event.preventDefault()

/**
 * The search field under the sidepanel toolbar. Enter chats about the text; Escape or the × clears it. A `/` opens
 * a popup of skills under the bar, picked with the arrows and Enter while typing carries on in the bar.
 */
export function SidebarSearchBar() {
  const [query, setQuery] = useSidebarSearchQuery()
  const [, setPickedSkillId] = useSidebarSearchSkillId()
  const [, setSkillHighlight] = useSidebarSkillHighlight()
  const slashSkills = useSidebarSlashSkills()
  const startChatAboutQuery = useStartChatAboutQuery()
  const inputRef = useRef<HTMLInputElement>(null)
  // Dismissing hides the popup until the text changes, so Escape or a click away doesn't fight the `/` still there.
  const [isSkillPopupDismissed, setIsSkillPopupDismissed] = useState(false)
  const isSkillPopupOpen = slashSkills.isOpen && slashSkills.skills.length > 0 && !isSkillPopupDismissed

  // The popup portals into the overlay, which stays on screen after the sidepanel slides away.
  useOnSidepanelHidden(useCallback(() => setIsSkillPopupDismissed(true), []))

  const updateQuery = (text: string) => {
    setQuery(text)
    slashSkills.forgetSkillUnlessNamed(text)
    setSkillHighlight(0)
    setIsSkillPopupDismissed(false)
  }

  const clearQuery = () => {
    updateQuery('')
    setPickedSkillId(null)
    inputRef.current?.focus()
  }

  const pickSkill = (skill: Skill) => {
    slashSkills.pickSkill(skill)
    inputRef.current?.focus()
  }

  const handleSkillPopupKey = (event: KeyboardEvent<HTMLInputElement>): boolean => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      slashSkills.moveHighlight(event.key === 'ArrowDown' ? 1 : -1)
      return true
    }
    if ((event.key === 'Enter' || event.key === 'Tab') && !event.nativeEvent.isComposing) {
      pickSkill(slashSkills.skills[slashSkills.highlightedIndex])
      return true
    }
    if (event.key === 'Escape') {
      event.stopPropagation()
      setIsSkillPopupDismissed(true)
      return true
    }
    return false
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (isSkillPopupOpen && handleSkillPopupKey(event)) {
      event.preventDefault()
      return
    }
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault()
      startChatAboutQuery()
      return
    }
    // Only swallow Escape while there's text to clear, so an empty field still lets it close the panel.
    if (event.key === 'Escape' && query) {
      event.preventDefault()
      event.stopPropagation()
      clearQuery()
    }
  }

  return (
    <div className="w-full px-3 pb-2">
      <Popover open={isSkillPopupOpen} onOpenChange={(open) => !open && setIsSkillPopupDismissed(true)}>
        <PopoverAnchor asChild>
          <div>
            <Input
              ref={inputRef}
              size="xl"
              type="text"
              className={YELLOW_FOCUS_RING}
              value={query}
              onChange={(event) => updateQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search or ask"
              aria-label="Search your meetings, or ask Highlight"
              aria-expanded={isSkillPopupOpen}
              spellCheck={false}
              left={<SidebarAddMenu />}
              right={
                <>
                  {query && (
                    <Button variant="ghost" size="sm" shape="square" aria-label="Clear search" onClick={clearQuery}>
                      <X />
                    </Button>
                  )}
                  <SidebarModelMenu />
                </>
              }
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          side="bottom"
          align="start"
          sideOffset={6}
          className="m-0 h-fit w-fit rounded-none border-none bg-transparent p-0 shadow-none"
          onOpenAutoFocus={keepFocusInBar}
          onCloseAutoFocus={keepFocusInBar}
          // Clicking the bar itself keeps the popup; only a click elsewhere dismisses it.
          onInteractOutside={(event) => {
            if (inputRef.current?.parentElement?.contains(event.target as Node)) event.preventDefault()
          }}
        >
          <SidebarSkillPopup
            skills={slashSkills.skills}
            highlightedIndex={slashSkills.highlightedIndex}
            onPick={pickSkill}
          />
        </PopoverContent>
      </Popover>
      <SidebarSearchAttachments />
    </div>
  )
}
